import React, { ComponentType, CSSProperties, useEffect, useRef, useState } from 'react';

export function withLazyLoad<
    TProps extends {
        className?: string | undefined;
        style?: CSSProperties | undefined;
    },
>(
    Component: ComponentType<TProps>,
    Placeholder: ComponentType<any>,
    options: IntersectionObserverInit = {
        root: null,
        rootMargin: '100% 200%',
        threshold: 0,
    },
): ComponentType<TProps> {
    return (props: TProps) => {
        const [isIntersected, setIsIntersected] = useState<boolean>(false);
        const placeholderRef = useRef<HTMLDivElement | null>(null);

        useEffect(() => {
            if (!placeholderRef.current || isIntersected) {
                return;
            }

            const observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry?.isIntersecting) {
                        setIsIntersected(true);
                    }
                });
            }, options);

            observer.observe(placeholderRef.current);

            return () => {
                if (placeholderRef.current) {
                    // eslint-disable-next-line react-hooks/exhaustive-deps
                    observer.unobserve(placeholderRef.current);
                }
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [placeholderRef.current, isIntersected, setIsIntersected]);

        if (isIntersected) {
            return <Component {...props} />;
        }

        return <Placeholder {...props} ref={placeholderRef} />;
    };
}
